/**
 * /support — ticket list + "new ticket" form for org members.
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, Plus, X, Paperclip, ImagePlus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import { watchTicketsForOrg } from '@/lib/firestore';
import { callCreateTicket, type AttachmentMeta } from '@/lib/functions';
import { uploadTicketAttachment } from '@/lib/storage';
import type { Ticket, TicketCategory, TicketStatus } from '@/types/models';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { fmtRelative } from '@/lib/utils';
import { cn } from '@/lib/utils';

const CATEGORIES: TicketCategory[] = ['platform', 'ai_system', 'account', 'other'];
const STATUSES: (TicketStatus | 'all')[] = ['all', 'open', 'in_progress', 'closed'];
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

function ticketBadgeVariant(status: TicketStatus) {
  switch (status) {
    case 'open': return 'warning' as const;
    case 'in_progress': return 'info' as const;
    case 'closed': return 'neutral' as const;
  }
}

interface AttachmentDraft { file: File; preview: string }

export default function Support() {
  const { effectiveOrgId } = useAuth();
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory>('platform');
  const [description, setDescription] = useState('');
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([]);
  const [attachError, setAttachError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [dragging, setDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return watchTicketsForOrg(effectiveOrgId, setTickets);
  }, [effectiveOrgId]);

  const filtered = tickets === null ? null : tickets.filter(
    ticket => statusFilter === 'all' || ticket.status === statusFilter,
  );

  const addFiles = (files: File[]) => {
    setAttachError('');
    const next = [...drafts];
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) { setAttachError(t('support.attach.error.type')); return; }
      if (file.size > MAX_SIZE_BYTES) { setAttachError(t('support.attach.error.size')); return; }
      if (next.length >= MAX_ATTACHMENTS) { setAttachError(t('support.attach.error.count')); return; }
      next.push({ file, preview: URL.createObjectURL(file) });
    }
    setDrafts(next);
  };

  const removeDraft = (idx: number) => {
    setDrafts(prev => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const closeForm = () => {
    drafts.forEach(d => URL.revokeObjectURL(d.preview));
    setSubject(''); setCategory('platform'); setDescription('');
    setDrafts([]); setAttachError(''); setFormError('');
    setDragging(false);
    dragCounter.current = 0;
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveOrgId) return;
    if (!subject.trim() || !description.trim()) return;

    setSubmitting(true);
    setFormError('');
    try {
      const attachmentMeta: AttachmentMeta[] = drafts.map(d => ({
        contentType: d.file.type,
        sizeBytes: d.file.size,
        fileName: d.file.name,
      }));

      const resp = await callCreateTicket({
        orgId: effectiveOrgId,
        subject,
        category,
        description,
        attachmentMeta,
      });

      const uploads = resp.data.uploads ?? [];
      if (uploads.length > 0) {
        await Promise.allSettled(
          drafts.map((d, i) => {
            const slot = uploads[i];
            if (!slot) return Promise.resolve();
            return uploadTicketAttachment(slot.path, d.file).promise;
          }),
        );
      }

      closeForm();
      navigate(`/support/${resp.data.ticketId}`);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes('Files')) setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    if (submitting || drafts.length >= MAX_ATTACHMENTS) return;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) addFiles(files);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('support.title')}</h1>
          <p className="mt-1 text-sm text-ink-500">{t('support.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 dark:bg-primary dark:hover:bg-primary/80"
        >
          <Plus className="h-4 w-4" />
          {t('support.new_ticket')}
        </button>
      </header>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition',
              statusFilter === s
                ? 'bg-brand-600 text-white dark:bg-primary'
                : 'border border-ink-200 text-ink-600 hover:bg-ink-50 dark:border-border dark:text-foreground/70 dark:hover:bg-secondary',
            )}
          >
            {t(`support.filter.${s}` as any)}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered === null ? (
        <Loader fullscreen={false} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<LifeBuoy className="h-5 w-5" />}
          title={t('support.empty.title')}
          description={t('support.empty.desc')}
          action={
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              {t('support.new_ticket')}
            </button>
          }
        />
      ) : (
        <Table>
          <TableHead>
            <tr>
              <TableHeadCell>{t('support.col.subject')}</TableHeadCell>
              <TableHeadCell>{t('support.col.category')}</TableHeadCell>
              <TableHeadCell>{t('support.col.status')}</TableHeadCell>
              <TableHeadCell>{t('support.col.updated')}</TableHeadCell>
            </tr>
          </TableHead>
          <TableBody>
            {filtered.map(ticket => (
              <TableRow
                key={ticket.id}
                onClick={() => navigate(`/support/${ticket.id}`)}
                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-secondary/40"
              >
                <TableCell className="max-w-[360px]">
                  <p className="truncate text-base font-semibold text-ink-900 dark:text-foreground">
                    {ticket.subject}
                  </p>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-ink-500 dark:text-muted-foreground">
                    {t(`support.category.${ticket.category}` as any)}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={ticketBadgeVariant(ticket.status)}>
                    {t(`support.status.${ticket.status}` as any)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-ink-500 dark:text-muted-foreground">
                  {fmtRelative(ticket.updatedAt, t as any, locale)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* New ticket modal */}
      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-card">
            <button
              onClick={closeForm}
              disabled={submitting}
              className="absolute right-4 top-4 text-ink-400 hover:text-ink-600 dark:text-muted-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="font-display text-xl text-ink-900 dark:text-white">{t('support.form.title')}</h2>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              {/* Subject */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700 dark:text-foreground/80">
                  {t('support.form.subject')} *
                </label>
                <input
                  type="text"
                  required
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder={t('support.form.subject.ph')}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-border dark:bg-background dark:text-foreground"
                />
              </div>

              {/* Category */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700 dark:text-foreground/80">
                  {t('support.form.category')}
                </label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value as TicketCategory)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-border dark:bg-background dark:text-foreground"
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{t(`support.category.${c}` as any)}</option>
                  ))}
                </select>
              </div>

              {/* Description (required) */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700 dark:text-foreground/80">
                  {t('support.form.description')} *
                </label>
                <textarea
                  required
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={t('support.form.description.ph')}
                  rows={4}
                  className="w-full resize-none rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-border dark:bg-background dark:text-foreground"
                />
              </div>

              {/* Attachments — click or drag & drop */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-700 dark:text-foreground/80">
                  {t('support.form.attachments')}
                </label>
                <div
                  onDragEnter={onDragEnter}
                  onDragLeave={onDragLeave}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  className={cn(
                    'relative rounded-lg border border-dashed px-3 py-4 transition',
                    dragging
                      ? 'border-brand-500 bg-brand-50 dark:border-primary dark:bg-primary/10'
                      : 'border-ink-300 dark:border-border',
                  )}
                >
                  {dragging && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 rounded-lg bg-brand-50/95 dark:bg-primary/15">
                      <ImagePlus className="h-6 w-6 text-brand-600 dark:text-primary" />
                      <span className="text-xs font-medium text-brand-700 dark:text-primary">
                        {t('support.attach.drop')}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={drafts.length >= MAX_ATTACHMENTS || submitting}
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 text-xs text-ink-500 hover:text-ink-700 disabled:opacity-40 dark:text-muted-foreground dark:hover:text-foreground"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    {t('support.attach.hint')}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={e => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
                  />
                </div>
                {drafts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {drafts.map((d, i) => (
                      <div key={i} className="relative">
                        <img src={d.preview} alt={d.file.name} className="h-14 w-14 rounded-lg object-cover border border-ink-200 dark:border-border" />
                        <button
                          type="button"
                          onClick={() => removeDraft(i)}
                          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#D9D4D9] text-ink-900 hover:bg-red-600 hover:text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {attachError && <p className="mt-1 text-xs text-red-600">{attachError}</p>}
              </div>

              {formError && <p className="text-sm text-red-600">{formError}</p>}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={submitting}
                  className="rounded-lg border border-ink-200 px-4 py-2 text-sm text-ink-600 hover:bg-ink-50 disabled:opacity-40 dark:border-border dark:text-foreground/70"
                >
                  {t('support.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting || !subject.trim() || !description.trim()}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40 dark:bg-primary"
                >
                  {submitting ? '…' : t('support.form.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
